import React from 'react';
import {
  Button,
  FormInputPassword,
  Icon,
  P,
} from '@fashionnovateam/nova-ui-toolkit';
import { Link } from 'react-router-dom';

export type PasswordFormProps = {
  isInfluencer: boolean;
  token: string;
  updatePassword: (token: string, password: string) => Promise<void>;
  onError: (title?: string, description?: string) => void;
};

export function PasswordForm(props: PasswordFormProps) {
  const { isInfluencer, token, updatePassword, onError } = props;

  const [loading, setLoading] = React.useState(false);
  const [errors, setErrors] = React.useState<{
    password?: string;
    rePassword?: string;
  }>({});
  const [resetSucceed, setResetSucceed] = React.useState(false);

  const validatePassword = (
    field: 'password' | 'rePassword',
    password: string,
  ) => {
    if (!password) {
      setErrors((prev) => ({
        ...prev,
        [field]: 'Please enter your new password.',
      }));
      return false;
    }
    if (password.length < 6) {
      setErrors((prev) => ({
        ...prev,
        [field]: 'Your password must be at least 6 characters.',
      }));
      return false;
    }
    setErrors((prev) => ({
      ...prev,
      [field]: undefined,
    }));
    return true;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onError();

    const form = event.currentTarget as HTMLFormElement;
    const password = form.password.value;
    const rePassword = form.rePassword.value;

    const validPassword = validatePassword('password', password);
    const validRePassword = validatePassword('rePassword', rePassword);
    if (!validPassword || !validRePassword) return;

    if (password !== rePassword) {
      onError(
        'Passwords Do Not Match',
        'The passwords you entered do not match. Please make sure both passwords are identical and try again.',
      );
      return;
    }

    try {
      setLoading(true);
      await updatePassword(token, password);
      setResetSucceed(true);
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordBlur = (password: string) => {
    validatePassword('password', password);
  };

  const handleRePasswordBlur = (rePassword: string) => {
    validatePassword('rePassword', rePassword);
  };

  if (resetSucceed) {
    return (
      <div className="flex flex-col gap-10">
        <div className="flex flex-col gap-2">
          <P size="xl" weight="medium">
            Password reset successfully
          </P>
          <P size="sm" color="disabled">
            Your password has been reset successfully. You can now sign in with
            your new password.
          </P>
        </div>
        <Link to={isInfluencer ? '/influencer-login' : '/login'}>
          <Button className="w-full" color="secondary">
            Back to Sign In
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <form className="flex flex-col gap-10" onSubmit={handleSubmit}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col hap-1">
          <P size="xl" weight="medium">
            Create your new password
          </P>
          <P size="sm" color="disabled">
            Must be at least 6 characters.
          </P>
        </div>
        <FormInputPassword
          label="New Password"
          name="password"
          inputId="password"
          disabled={loading}
          error={errors.password}
          onBlurValue={handlePasswordBlur}
        />
        <FormInputPassword
          label="Confirm New Password"
          name="rePassword"
          inputId="rePassword"
          disabled={loading}
          error={errors.rePassword}
          onBlurValue={handleRePasswordBlur}
        />
      </div>
      <Button className="w-full" type="submit" loading={loading}>
        Continue
        <Icon.ArrowRight />
      </Button>
    </form>
  );
}
